/**
 * The type of (redux) action to update visitors count.
 *
 * {
 *     type: UPDATE_VISITORS_COUNT,
 *     count: number
 * }
 */
export const UPDATE_VISITORS_COUNT = 'UPDATE_VISITORS_COUNT';
/**
 * The type of (redux) action which enables/disables visitors UI mode.
 *
 * {
 *     type: I_AM_VISITOR_MODE,
 *     enabled: boolean
 * }
 */
export const I_AM_VISITOR_MODE = 'I_AM_VISITOR_MODE';
/**
 * The type of (redux) action which indicates that a promotion request was received from a visitor.
 *
 * {
 *     type: VISITOR_PROMOTION_REQUEST,
 *     nick: string,
 *     from: string
 * }
 */
export const VISITOR_PROMOTION_REQUEST = 'VISITOR_PROMOTION_REQUEST';
/**
 * The type of (redux) action which indicates that a promotion response denied was received.
 *
 * {
 *     type: CLEAR_VISITOR_PROMOTION_REQUEST,
 *     request: IPromotionRequest
 * }
 */
export const CLEAR_VISITOR_PROMOTION_REQUEST = 'CLEAR_VISITOR_PROMOTION_REQUEST';
/**
 * The type of (redux) action which shows or hides the loading screen for a
 * move between the main meeting and viewer mode.
 *
 * {
 *     type: SET_ROLE_TRANSITION,
 *     transition: IRoleTransition | undefined
 * }
 */
export const SET_ROLE_TRANSITION = 'SET_ROLE_TRANSITION';
/**
 * The type of (redux) action which sets visitor demote actor.
 *
 * {
 *     type: SET_VISITOR_DEMOTE_ACTOR,
 *     displayName: string
 * }
 */
export const SET_VISITOR_DEMOTE_ACTOR = 'SET_VISITOR_DEMOTE_ACTOR';
/**
 * The type of (redux) action which sets visitors support.
 *
 * {
 *     type: SET_VISITORS_SUPPORTED,
 *     value: string
 * }
 */
export const SET_VISITORS_SUPPORTED = 'SET_VISITORS_SUPPORTED';
/**
 * The type of (redux) action which stores whether visitor mode is currently armed for the room
 * (as reported by backend room metadata), and the active toggle mode.
 *
 * {
 *     type: SET_VISITORS_ENABLED,
 *     enabled: boolean,
 *     mode: string | undefined,
 *     updatedBy: string | undefined,
 *     updatedAt: number | undefined
 * }
 */
export const SET_VISITORS_ENABLED = 'SET_VISITORS_ENABLED';
/**
 * The type of (redux) action which marks a moderator visitor-mode toggle request as pending or settled.
 *
 * {
 *     type: SET_VISITOR_MODE_TOGGLE_PENDING,
 *     pending: boolean,
 *     requestedValue: boolean | undefined,
 *     requestedMode: string | undefined
 * }
 */
export const SET_VISITOR_MODE_TOGGLE_PENDING = 'SET_VISITOR_MODE_TOGGLE_PENDING';
/**
 * The type of (redux) action which stores the latest moderator visitor-mode toggle error.
 *
 * {
 *     type: SET_VISITOR_MODE_TOGGLE_ERROR,
 *     error: string | undefined
 * }
 */
export const SET_VISITOR_MODE_TOGGLE_ERROR = 'SET_VISITOR_MODE_TOGGLE_ERROR';
/**
 * The type of (redux) action which records the room's confirmed auto-promote
 * (auto-admit visitor join requests) state, as read from room metadata
 * {@code visitors.autoPromote}. This is server truth, never set optimistically.
 *
 * {
 *     type: SET_AUTO_PROMOTE_VISITORS,
 *     value: boolean | undefined
 * }
 */
export const SET_AUTO_PROMOTE_VISITORS = 'SET_AUTO_PROMOTE_VISITORS';
/**
 * The type of (redux) action which marks a moderator auto-promote toggle request
 * as in flight (or settles it).
 *
 * {
 *     type: SET_AUTO_PROMOTE_TOGGLE_PENDING,
 *     pending: boolean,
 *     requestedValue: boolean | undefined
 * }
 */
export const SET_AUTO_PROMOTE_TOGGLE_PENDING = 'SET_AUTO_PROMOTE_TOGGLE_PENDING';
/**
 * The type of (redux) action which stores the latest moderator auto-promote
 * toggle error.
 *
 * {
 *     type: SET_AUTO_PROMOTE_TOGGLE_ERROR,
 *     error: string | undefined
 * }
 */
export const SET_AUTO_PROMOTE_TOGGLE_ERROR = 'SET_AUTO_PROMOTE_TOGGLE_ERROR';
/**
 * The type of (redux) action which records the server-confirmed chat-suspension
 * state of one visitor (identified by their vid). Broadcast by Prosody to all
 * moderators, so every moderator UI stays in sync.
 *
 * {
 *     type: SET_VISITOR_CHAT_SUSPENDED,
 *     vid: string,
 *     on: boolean
 * }
 */
export const SET_VISITOR_CHAT_SUSPENDED = 'SET_VISITOR_CHAT_SUSPENDED';
/**
 * The type of (redux) action recording the moderator's pre-join visitor mode
 * choice, and whether the request to the server is still in flight.
 *
 * {
 *     type: SET_PREJOIN_VISITORS_MODE,
 *     enabled: boolean | undefined,
 *     pending: boolean
 * }
 */
export const SET_PREJOIN_VISITORS_MODE = 'SET_PREJOIN_VISITORS_MODE';
/**
 * The type of (redux) action which stores the server-reported roster of the
 * visitors currently on the room's visitor nodes.
 *
 * {
 *     type: SET_VISITORS_ROSTER,
 *     visitors: IVisitor[]
 * }
 */
export const SET_VISITORS_ROSTER = 'SET_VISITORS_ROSTER';
/**
 * The type of (redux) action which adds (or replaces) one visitor in the
 * server-reported visitors roster.
 *
 * {
 *     type: VISITOR_JOINED,
 *     visitor: IVisitor
 * }
 */
export const VISITOR_JOINED = 'VISITOR_JOINED';
/**
 * The type of (redux) action which removes one visitor from the
 * server-reported visitors roster.
 *
 * {
 *     type: VISITOR_LEFT,
 *     vid: string
 * }
 */
export const VISITOR_LEFT = 'VISITOR_LEFT';
/**
 * The type of (redux) action which records the server-confirmed raise-hand
 * (ask-to-join) block state of one visitor, identified by their vid.
 *
 * {
 *     type: SET_VISITOR_HAND_BLOCKED,
 *     vid: string,
 *     on: boolean
 * }
 */
export const SET_VISITOR_HAND_BLOCKED = 'SET_VISITOR_HAND_BLOCKED';
/**
 * The type of (redux) action which stores whether the local visitor's raise
 * hand was blocked by a moderator. Server-confirmed values only.
 *
 * {
 *     type: SET_I_AM_HAND_BLOCKED,
 *     on: boolean
 * }
 */
export const SET_I_AM_HAND_BLOCKED = 'SET_I_AM_HAND_BLOCKED';
/**
 * The type of (redux) action which stores whether the local visitor's chat
 * was suspended by a moderator. Server-confirmed values only.
 *
 * {
 *     type: SET_I_AM_CHAT_SUSPENDED,
 *     on: boolean
 * }
 */
export const SET_I_AM_CHAT_SUSPENDED = 'SET_I_AM_CHAT_SUSPENDED';
/**
 * The type of (redux) action which records the room's confirmed raise-hand
 * disable state for viewers, as read from room metadata
 * {@code visitors.raiseHandDisabled}. This is server truth.
 *
 * {
 *     type: SET_VISITORS_RAISE_HAND_DISABLED,
 *     value: boolean | undefined
 * }
 */
export const SET_VISITORS_RAISE_HAND_DISABLED = 'SET_VISITORS_RAISE_HAND_DISABLED';
/**
 * The type of (redux) action which marks a moderator room-wide raise-hand
 * block toggle request as in flight (or settles it).
 *
 * {
 *     type: SET_RAISE_HAND_DISABLED_TOGGLE_PENDING,
 *     pending: boolean,
 *     requestedValue: boolean | undefined
 * }
 */
export const SET_RAISE_HAND_DISABLED_TOGGLE_PENDING = 'SET_RAISE_HAND_DISABLED_TOGGLE_PENDING';
/**
 * The type of (redux) action which stores the latest moderator raise-hand
 * block toggle error.
 *
 * {
 *     type: SET_RAISE_HAND_DISABLED_TOGGLE_ERROR,
 *     error: string | undefined
 * }
 */
export const SET_RAISE_HAND_DISABLED_TOGGLE_ERROR = 'SET_RAISE_HAND_DISABLED_TOGGLE_ERROR';
